import { SmallEmeraldButton } from '../../../../components/buttons';
import { Stat } from '../../../../components/stat';
import { fullCurrencyFormatter } from '../../../../helpers/format-currency';
import { formatCases, formatDoh } from '../../../../helpers/format-quantity';
import type { ProductOrderItem } from '../data/product-order-item';
import type { Family } from '../hooks/use-families';
import { familyTotals } from '../utils/family-aggregates';
import { FamilySkuTable } from './family-sku-table';
import { FamilyTierBar } from './family-tier-bar';

type Props = {
  family: Family;
  tierSlotCount: number;
  setOrderItem: (item: ProductOrderItem) => void;
  allocate: (family: Family, targetCases: number) => void;
  onRemoveMember: (item: ProductOrderItem) => void;
  onAddProductClicked: (family: Family) => void;
};

/**
 * Always-expanded family card. Three-zone header grid (title left / stats
 * centered / action column right), tier shortcut bar, fat-row SKU table.
 * Collapse/expand removed per v2 spec — deal buying is scan-and-compare.
 */
export function FamilyCard({
  family,
  tierSlotCount,
  setOrderItem,
  allocate,
  onRemoveMember,
  onAddProductClicked,
}: Props) {
  const { totalCases, savingsDollars, savingsPercent, extendedDollars, minDOH } = familyTotals(
    family.members
  );

  return (
    <div className="rounded border border-gray-200 bg-white shadow-sm">
      <div
        className="grid items-center gap-4 border-b border-gray-200 px-3 py-2.5"
        style={{ gridTemplateColumns: '1fr auto 1fr' }}
      >
        <div>
          <div className="font-semibold">{family.name}</div>
          <div className="text-xs text-gray-500">{family.members.length} SKUs</div>
        </div>
        <div className="flex gap-5 justify-self-center">
          <Stat label="DOH min" value={minDOH === null ? '—' : `${formatDoh(minDOH)}d`} />
          <Stat label="Cases" value={formatCases(totalCases)} />
          <Stat label="Extended" value={fullCurrencyFormatter(extendedDollars)} />
          <Stat label="Savings %" value={`${savingsPercent.toFixed(1)}%`} accent />
          <Stat label="Savings $" value={fullCurrencyFormatter(savingsDollars)} accent />
        </div>
        <div className="justify-self-end">
          <SmallEmeraldButton
            onClick={() => onAddProductClicked(family)}
            disabled={family.candidates.length === 0}
            title={
              family.candidates.length > 0 ? undefined : 'No other products from this brand to add'
            }
          >
            + Add product
          </SmallEmeraldButton>
        </div>
      </div>

      <div className="px-3 pt-2.5">
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          Set family total
        </div>
        <FamilyTierBar
          members={family.members}
          totalCases={totalCases}
          onClickTier={(target) => allocate(family, target)}
          onClickDaysSupply={(memberCases) => {
            for (const { member, targetCases } of memberCases) {
              setOrderItem({ ...member, cases: targetCases, units: 0 });
            }
          }}
        />
      </div>

      <div className="p-3">
        <FamilySkuTable
          family={family}
          familyCases={totalCases}
          tierSlotCount={tierSlotCount}
          setOrderItem={setOrderItem}
          onRemove={onRemoveMember}
        />
      </div>
    </div>
  );
}
